import { colours } from "@shared/ui.design-system";
import { CloseIcon } from "@shared/ui.icons";
import { clsx } from "clsx";
import React from "react";

import { Typography } from "../Typography/Typography";

import * as styles from "./Modal.css";
import { useModalContext } from "./context";
import { ModalHeaderProps } from "./types";

/**
 * Header component for the Modal
 *
 * Renders either:
 * - A title and close button when provided with a string
 * - Custom content when provided with a React node
 *
 * @param handleDismiss - Function to call when the close button is clicked
 * @param id - Unique ID for the header element (used for accessibility)
 * @returns {JSX.Element | null} The header component or null
 */
export const ModalHeader: React.FC<ModalHeaderProps> = ({
  children,
  handleDismiss,
  id
}) => {
  const { showTopBorder } = useModalContext();

  if (typeof children === "undefined") return null;
  if (typeof children !== "string")
    return (
      <header
        className={clsx(styles.header, showTopBorder && styles.headerActive)}
      >
        {children}
      </header>
    );
  return (
    <header
      className={clsx(styles.header, showTopBorder && styles.headerActive)}
      data-testid="modal-header"
      id={id}
    >
      <Typography as="h3" variant="subheader-3">
        {children}
      </Typography>
      <button
        onClick={() => handleDismiss?.()}
        aria-label="Close modal"
        className={styles.closeButton}
      >
        <CloseIcon color={colours.primary.dark} className={styles.closeIcon} />
      </button>
    </header>
  );
};
